 import { useState, useEffect, useCallback } from 'react';
 import { message } from 'antd';
 import { emailConfigApi } from '../lib/api';
 import { templateTypeLabels } from '../data/emailTemplates';
 import moment from 'moment';
 
 export const useEmailTemplates = () => {
   const [templates, setTemplates] = useState([]);
   const [isLoading, setIsLoading] = useState(true);
   const [isSaving, setIsSaving] = useState(false);
   const [error, setError] = useState(null);
 
   // Fetch templates from API
   const fetchTemplates = useCallback(async () => {
     setIsLoading(true);
     setError(null);
     
     try {
       const data = await emailConfigApi.getAll();
       // Transform API data to match component structure
       const transformed = data.map((item) => ({
         id: item.id,
         type: item.type,
         name: item.template_name,
         subject: item.email_subject,
         content: item.email_content,
         receiverEmails: item.configured_emails || [],
         lastModified: moment(item.updated_at).format("DD/MM/YYYY"),
       }));
       setTemplates(transformed);
     } catch (err) {
       setError(err.message);
       message.error('Failed to load email templates');
     } finally {
       setIsLoading(false);
     }
   }, []);
 
   // Initial fetch
   useEffect(() => {
     fetchTemplates();
   }, [fetchTemplates]);
 
   // Update template locally (for editing before save)
   const updateTemplateLocally = useCallback((type, updates) => {
     setTemplates((prev) =>
       prev.map((t) => (t.type === type ? { ...t, ...updates } : t))
     );
   }, []);
 
   // Save template to API
   const saveTemplate = useCallback(async (type) => {
     const template = templates.find((t) => t.type === type);
     if (!template) return false;
 
     // Validate emails
     const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
     const invalidEmails = template.receiverEmails.filter(
       (email) => email && !emailRegex.test(email)
     );
     
     if (invalidEmails.length > 0) {
       message.error(`Invalid email addresses: ${invalidEmails.join(', ')}`);
       return false;
     }
 
     setIsSaving(true);
     
     try {
       await emailConfigApi.update(template.id, {
         email_subject: template.subject,
         email_content: template.content,
         configured_emails: template.receiverEmails.filter(Boolean),
       });
       
       // Update lastModified
       updateTemplateLocally(type, { lastModified: moment().format("DD/MM/YYYY") });
       message.success(`${templateTypeLabels[type]} saved successfully!`);
       return true;
     } catch (err) {
       message.error('Failed to save template');
       return false;
     } finally {
       setIsSaving(false);
     }
   }, [templates, updateTemplateLocally]);
 
   // Reset template to server version
   const resetTemplate = useCallback(async (type) => {
     await fetchTemplates();
     message.info('Template reset to last saved version');
   }, [fetchTemplates]);
 
   // Get template by type
   const getTemplate = useCallback(
     (type) => templates.find((t) => t.type === type) || null,
     [templates]
   );
 
   return {
     templates,
     isLoading,
     isSaving,
     error,
     getTemplate,
     updateTemplateLocally,
     saveTemplate,
     resetTemplate,
     refetch: fetchTemplates,
   };
 };